import React, { useEffect, useMemo, useState } from 'react';
import { Modal, message } from 'antd';
import { Visibility } from 'mock/folder.data';
import {
  assignAccess_File,
  assignAccess_Folder,
  getLibraryContent,
  getShareDetail,
  getUser,
  revokeAccess_File,
  revokeAccess_Folder,
} from '../function/api';
import { UserWithEmail } from '../function/type';

interface Props {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  itemVisible: Visibility;
  itemType: 'file' | 'folder';
}

const ShareModal: React.FC<Props> = ({ open, onClose, itemId, itemName, itemVisible, itemType }) => {
  const [search, setSearch] = useState('');
  const [pendingUser, setPendingUser] = useState<UserWithEmail | null>(null);
  const [users, setUsers] = useState<UserWithEmail[]>([]);
  const [sharedUsers, setSharedUsers] = useState<UserWithEmail[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [submittingUserId, setSubmittingUserId] = useState<number | null>(null);

  const numericItemId = useMemo(() => Number(itemId), [itemId]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setPendingUser(null);
      setUsers([]);
      setSharedUsers([]);
      setSearchError(null);
      setSearching(false);
      setSubmittingUserId(null);
      return;
    }

    if (canSubmit) {
      getShareDetail(numericItemId, itemType)
        .then((res) => {
          if (res && res.data) {
            const list = Array.isArray(res.data) ? res.data : [];
            const mapped: UserWithEmail[] = list.map((item: any) => ({
              id: item.user_id || item.id,
              email: item.email || item.user_email || `User #${item.user_id || item.id}`,
              avatar: item.avatar ?? null,
            }));
            setSharedUsers(mapped);
          }
        })
        .catch((err) => {
          console.error('Lỗi lấy thông tin chia sẻ:', err);
        });
    }

    const normalizedSearch = search.trim();
    if (!normalizedSearch) {
      setUsers([]);
      setSearchError(null);
      setSearching(false);
      return;
    }

    let isDisposed = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setSearching(true);
        setSearchError(null);
        const response = await getUser(normalizedSearch);
        if (!isDisposed) {
          setUsers(response);
        }
      } catch (error) {
        if (!isDisposed) {
          setUsers([]);
          setSearchError(error instanceof Error ? error.message : 'Không thể tìm kiếm người dùng.');
        }
      } finally {
        if (!isDisposed) {
          setSearching(false);
        }
      }
    }, 300);

    return () => {
      isDisposed = true;
      window.clearTimeout(timeoutId);
    };
  }, [open, search]);

  const sharedUserIds = useMemo(() => new Set(sharedUsers.map((user) => user.id)), [sharedUsers]);

  const canSubmit = Number.isFinite(numericItemId);

  const applyAccessChangeRecursive = async (folderId: number, userId: number, nextAction: 'assign' | 'revoke') => {
    // Perform access change on the target folder itself
    if (nextAction === 'assign') {
      const res = await assignAccess_Folder(folderId, userId);
      if (res && res.status === false) {
        throw new Error(res.message || res.msg || 'Chia sẻ thư mục thất bại.');
      }
    } else {
      const res = await revokeAccess_Folder(folderId, userId);
      if (res && res.status === false) {
        throw new Error(res.message || res.msg || 'Thu hồi thư mục thất bại.');
      }
    }

    // Traverse recursively into subfolders and files
    const traverseAndApply = async (currentFolderId: number) => {
      try {
        const content = await getLibraryContent({ parent_id: currentFolderId });
        if (!content || !content.data) return;

        const { folders, documents } = content.data;

        // Apply access change for all files in current folder
        if (Array.isArray(documents)) {
          for (const doc of documents) {
            if (nextAction === 'assign') {
              await assignAccess_File(doc.id, userId);
            } else {
              await revokeAccess_File(doc.id, userId);
            }
          }
        }

        // Apply access change and recurse for all child folders
        if (Array.isArray(folders)) {
          for (const subFolder of folders) {
            if (nextAction === 'assign') {
              await assignAccess_Folder(subFolder.id, userId);
            } else {
              await revokeAccess_Folder(subFolder.id, userId);
            }
            await traverseAndApply(subFolder.id);
          }
        }
      } catch (err) {
        console.error(`Lỗi khi duyệt thư mục ID ${currentFolderId}:`, err);
      }
    };

    await traverseAndApply(folderId);
  };

  const applyAccessChange = async (user: UserWithEmail, nextAction: 'assign' | 'revoke') => {
    if (!canSubmit) {
      message.error('ID dữ liệu không hợp lệ.');
      return;
    }

    try {
      setSubmittingUserId(user.id);

      if (itemType === 'file') {
        let res: any;
        if (nextAction === 'assign') {
          res = await assignAccess_File(numericItemId, user.id);
        } else {
          res = await revokeAccess_File(numericItemId, user.id);
        }
        if (res && res.status === false) {
          throw new Error(res.message || res.msg || (nextAction === 'assign' ? 'Chia sẻ thất bại.' : 'Thu hồi thất bại.'));
        }
      } else {
        await applyAccessChangeRecursive(numericItemId, user.id, nextAction);
      }

      if (nextAction === 'assign') {
        setSharedUsers((prev) => (prev.some((item) => item.id === user.id) ? prev : [...prev, user]));
        message.success('Đã chia sẻ thành công.');
      } else {
        setSharedUsers((prev) => prev.filter((item) => item.id !== user.id));
        message.success('Đã thu hồi quyền truy cập.');
      }
    } catch (error) {
      const nextErrorMessage = error instanceof Error
        ? error.message
        : nextAction === 'assign'
          ? 'Không thể chia sẻ dữ liệu.'
          : 'Không thể thu hồi quyền truy cập.';
      message.error(nextErrorMessage);
    } finally {
      setSubmittingUserId(null);
    }
  };

  const handleShare = (user: UserWithEmail) => {
    if (itemVisible === 'public') {
      void applyAccessChange(user, 'assign');
    } else {
      setPendingUser(user);
    }
  };

  const handleConfirmShare = () => {
    if (!pendingUser) {
      return;
    }

    const nextPendingUser = pendingUser;
    setPendingUser(null);
    void applyAccessChange(nextPendingUser, 'assign');
  };

  const handleClose = () => {
    setSearch('');
    setPendingUser(null);
    setUsers([]);
    setSearchError(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={`Chia sẻ: ${itemName}`}
        open={open}
        onCancel={handleClose}
        footer={null}
        width={480}
      >
        <div className="share-modal">
          <div className="share-modal__search">
            <svg className="share-modal__search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              className="share-modal__search-input"
              placeholder="Nhập Gmail người dùng..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {sharedUsers.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-bold px-2 pt-2 pb-1 my-0">Đã chia sẻ</div>
              <div className="share-modal__list">
                {sharedUsers.map((user) => (
                  <div key={`shared-${user.id}`} className="share-modal__user">
                    <div className="share-modal__avatar">{user.email[0]?.toUpperCase() ?? 'U'}</div>
                    <div className="share-modal__user-info">
                      <span className="share-modal__user-name">{user.email}</span>
                      {/* <span className="share-modal__user-email">User #{user.id}</span> */}
                    </div>
                    <button
                      className="share-modal__share-btn"
                      onClick={() => void applyAccessChange(user, 'revoke')}
                      disabled={submittingUserId === user.id}
                    >
                      {submittingUserId === user.id ? 'Đang xử lý...' : 'Thu hồi'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="share-modal__list">
            {!search.trim() ? (
              <div className="share-modal__empty">Nhập Gmail để tìm kiếm người dùng</div>
            ) : searching ? (
              <div className="share-modal__empty">Đang tìm kiếm người dùng...</div>
            ) : searchError ? (
              <div className="share-modal__empty">{searchError}</div>
            ) : users.length === 0 ? (
              <div className="share-modal__empty">Không tìm thấy người dùng nào</div>
            ) : (
              users.map((user) => (
                <div key={user.id} className="share-modal__user">
                  <div className="share-modal__avatar">{user.email[0]?.toUpperCase() ?? 'U'}</div>
                  <div className="share-modal__user-info">
                    <span className="share-modal__user-name">{user.email}</span>
                  </div>
                  <button
                    className="share-modal__share-btn"
                    onClick={() => (sharedUserIds.has(user.id)
                      ? void applyAccessChange(user, 'revoke')
                      : handleShare(user))}
                    disabled={submittingUserId === user.id}
                  >
                    {submittingUserId === user.id
                      ? 'Đang xử lý...'
                      : sharedUserIds.has(user.id)
                        ? 'Thu hồi'
                        : 'Chia sẻ'}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={pendingUser !== null}
        title="Xác nhận chia sẻ"
        onCancel={() => setPendingUser(null)}
        onOk={handleConfirmShare}
        okText="Tiếp tục"
        cancelText="Hủy"
        confirmLoading={pendingUser !== null && submittingUserId === pendingUser.id}
        width={400}
      >
        <p>Đây là nội dung riêng tư. Bạn vẫn muốn cấp quyền truy cập cho {pendingUser?.email ?? 'người dùng này'}?</p>
      </Modal>
    </>
  );
};

export default ShareModal;
